import { memo } from "react";
import { Sparkles } from "lucide-react";
import {
  COMMON_TIMEZONES,
  COMPANY_SIZES,
  ONBOARDING_MODE_FULL,
  ONBOARDING_MODE_LITE,
  SUGGESTION_FIELDS,
} from "./onboardingConfigSchema";

const inputClass = "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white";

function SuggestionValue({ field, meta, disabled, onEdit }) {
  if (meta.multiline) {
    return (
      <textarea
        className={`${inputClass} min-h-[72px]`}
        value={field.value}
        disabled={disabled}
        maxLength={2000}
        onChange={(e) => onEdit(meta.key, e.target.value)}
      />
    );
  }
  if (meta.mode) {
    return (
      <select
        className={inputClass}
        value={field.value}
        disabled={disabled}
        onChange={(e) => onEdit(meta.key, e.target.value)}
      >
        <option value={ONBOARDING_MODE_LITE}>LITE</option>
        <option value={ONBOARDING_MODE_FULL}>FULL</option>
      </select>
    );
  }
  if (meta.key === "company_size") {
    return (
      <select
        className={inputClass}
        value={field.value}
        disabled={disabled}
        onChange={(e) => onEdit(meta.key, e.target.value)}
      >
        {COMPANY_SIZES.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
    );
  }
  if (meta.key === "timezone") {
    const zones = COMMON_TIMEZONES.includes(field.value)
      ? COMMON_TIMEZONES
      : [field.value, ...COMMON_TIMEZONES];
    return (
      <select
        className={inputClass}
        value={field.value}
        disabled={disabled}
        onChange={(e) => onEdit(meta.key, e.target.value)}
      >
        {zones.map((zone) => (
          <option key={zone} value={zone}>
            {zone}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      className={`${inputClass} ${meta.color ? "font-mono" : ""}`}
      value={field.value}
      disabled={disabled}
      maxLength={meta.color ? 7 : 80}
      spellCheck={!meta.color}
      onChange={(e) => onEdit(meta.key, e.target.value)}
    />
  );
}

function OnboardingSuggestionPanel({
  brief,
  briefError,
  suggestion,
  generating,
  disabled,
  pendingCount,
  acceptedCount,
  onBriefChange,
  onGenerate,
  onEdit,
  onDecide,
  onDismiss,
  onSave,
}) {
  const fields = suggestion?.fields || {};
  const visible = SUGGESTION_FIELDS.filter((meta) => fields[meta.key]);

  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-3" aria-label="AI suggestions">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">AI suggestions</p>
        <p className="mt-1 text-xs text-slate-500">
          Suggestions stay here until you accept or reject them and save. Nothing is written automatically.
        </p>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
        <label className="min-w-0 flex-1">
          <span className="sr-only">Context for suggestions</span>
          <input
            className={inputClass}
            value={brief}
            disabled={disabled || generating}
            maxLength={500}
            placeholder="Optional context (tone, audience…)"
            onChange={(e) => onBriefChange(e.target.value)}
          />
        </label>
        <button
          type="button"
          disabled={disabled || generating}
          className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-[#004f73] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          onClick={onGenerate}
        >
          <Sparkles className="h-4 w-4" aria-hidden />
          {generating ? "Generating…" : "Generate suggestions"}
        </button>
      </div>
      {briefError ? <p className="text-xs text-red-600">{briefError}</p> : null}

      {visible.length ? (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Source: {suggestion.source === "gemini" ? "AI" : "suggested defaults"}. Review each field before saving.
          </p>
          {visible.map((meta) => {
            const field = fields[meta.key];
            const accepted = field.decision === "accept";
            const rejected = field.decision === "reject";
            return (
              <div
                key={meta.key}
                className={`rounded-xl border bg-white p-3 ${
                  accepted ? "border-emerald-200" : rejected ? "border-slate-200 opacity-60" : "border-slate-200"
                }`}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-slate-800">{meta.label}</p>
                  <span className="text-[11px] uppercase tracking-wide text-slate-400">{field.decision}</span>
                </div>
                <SuggestionValue field={field} meta={meta} disabled={disabled || generating} onEdit={onEdit} />
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    disabled={disabled || generating || accepted}
                    className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 disabled:opacity-50"
                    onClick={() => onDecide(meta.key, "accept")}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    disabled={disabled || generating || rejected}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 disabled:opacity-50"
                    onClick={() => onDecide(meta.key, "reject")}
                  >
                    Reject
                  </button>
                </div>
              </div>
            );
          })}
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              disabled={disabled || generating}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600"
              onClick={onDismiss}
            >
              Close review
            </button>
            <button
              type="button"
              disabled={disabled || generating || pendingCount > 0 || acceptedCount === 0}
              className="rounded-lg bg-[#004f73] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              onClick={onSave}
            >
              Save accepted suggestions
            </button>
          </div>
          {pendingCount > 0 ? (
            <p className="text-xs text-amber-800">Accept or reject the remaining suggestions before saving.</p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export default memo(OnboardingSuggestionPanel);
